import { hsl, hslToColorString, parseToHsl } from "polished";

import { TRACK_TYPE } from "@/settings/settings";

export interface IKeyAndScheme {
  key: string;
  schemeIndex?: number;
  isDynamic?: boolean;
}

export interface IColorScheme {
  darker: string;
  dark: string;
  medium: string;
  light: string;
  lighter: string;
  lightest: string;
}

// extend this with unique FeatureTypes as required.
export const FeatureType = {
  GENE: "gene",
  TRANSCRIPT_LOCATION: "transcriptLocation",
  PROTEIN_LOCATION: "proteinLocation",
  VARIANT_POINT: "variant.point",
  VARIANT_INDEL: "variant.indel",
  VARIANT_INSERTION: "variant.insertion",
  VARIANT_DELETION: "variant.deletion",
  TRANSLOCATION_DELETION: "variant.translocation-deletion",
  TRANSLOCATION_INSERTION: "variant.translocation-insertion",
  BLAST: "blast",
  NUFEATURE: TRACK_TYPE.NUFEATURE,
  AAFEATURE: TRACK_TYPE.AAFEATURE,
  MARKER: TRACK_TYPE.MARKER_NUS,
  BED: TRACK_TYPE.BED,
  COORDINATE: TRACK_TYPE.COORDINATE,
};

// extend this with FeatureType assignments
export const FeatureType2ColorScheme: IKeyAndScheme[] = [
  { key: FeatureType.GENE, schemeIndex: 3 },
  { key: FeatureType.TRANSCRIPT_LOCATION, schemeIndex: 6 },
  { key: FeatureType.PROTEIN_LOCATION, schemeIndex: 5 },
  { key: FeatureType.VARIANT_POINT, schemeIndex: 2 },
  { key: FeatureType.VARIANT_INDEL, schemeIndex: 7 },
  { key: FeatureType.VARIANT_INSERTION, schemeIndex: 4 },
  { key: FeatureType.VARIANT_DELETION, schemeIndex: 6 },
  { key: FeatureType.TRANSLOCATION_DELETION, schemeIndex: 1 },
  { key: FeatureType.TRANSLOCATION_INSERTION, schemeIndex: 1 },
  { key: FeatureType.BLAST, isDynamic: true },
  { key: FeatureType.NUFEATURE, schemeIndex: 1 },
  { key: FeatureType.AAFEATURE, schemeIndex: 9 },
  { key: FeatureType.MARKER, schemeIndex: 1 },
  { key: FeatureType.BED, schemeIndex: 0 },
  { key: FeatureType.COORDINATE, schemeIndex: 0 },
];

export const ColorSchemes: IColorScheme[] = [
  { darker: "#4c6b98", dark: "#000", medium: "#000", light: "#e6eaf2", lighter: "#f6faf2", lightest: "#fff" }, // 0 for smaller genes and features (two colors only)
  { darker: "#3a3062", dark: "#5e4fa2", medium: "#aea7d0", light: "#d7d3e8", lighter: "#cec7f0", lightest: "#fff" }, // 1 blueberry
  { darker: "#123144", dark: "#3288bd", medium: "#84b8d7", light: "#cce1ee", lighter: "#d9f3f7", lightest: "#fff" }, // 2 windows-blue
  { darker: "#204f40", dark: "#66c2a5", medium: "#9bd7c4", light: "#d1ece4", lighter: "#cbf7f4", lightest: "#fff" }, // 3 pale-teal
  { darker: "#4c5b1e", dark: "#a9c44f", medium: "#cbdb96", light: "#e9f0d3", lighter: "#ebfbc6", lightest: "#fff" }, // 4 olive-green
  { darker: "#4c0e28", dark: "#b3215d", medium: "#d88fae", light: "#e3bbcc", lighter: "#f8bfde", lightest: "#fff" }, // 5 lipstick
  { darker: "#670f1a", dark: "#e54658", medium: "#ef909b", light: "#f4cfd3", lighter: "#ffd0db", lightest: "#fff" }, // 6 pig-pink
  { darker: "#722007", dark: "#f57f5b", medium: "#f8ac94", light: "#fbd2c6", lighter: "#fce1c8", lightest: "#fff" }, // 7 orange
  { darker: "#775a02", dark: "#fbc938", medium: "#fcdc7d", light: "#fff1c8", lighter: "#fcfcad", lightest: "#fff" }, // 8 butterscotch
  { darker: "#630c30", dark: "#892951", medium: "#d082a3", light: "#e6b2c8", lighter: "#f8bfde", lightest: "#fff" }, // 9 slightly lighter that protein for aaFeatures (5: Lipstick)
];

export interface IColorSchemeForType {
  type: string;
  colorScheme: IColorScheme;
}

export const selectedColorScheme: IColorScheme = {
  darker: "#0f2738", // not a TNSD color
  dark: "#2872a6", // not a TNSD color
  medium: "#42affa", // $color-dodger-blue
  light: "#d7edfc", // not a TNSD color
  lighter: "#62cffa",
  lightest: "#fff",
};

export const SEQUENCE_TYPE_COLORS = {
  // Contigs
  1: "#999999", // linear
  3: "#999999", // circular
  //Vectors
  14: "#AAAAAA",
  // Proteins
  2: "#E3BBCC",
  // Transcripts, Group 1
  4: "#F8AC94", // mRNA
  // Transcripts, Group 2
  5: "#AEA7D0", // rRNA
  6: "#5E4FA2", // tRNA
  // Transcripts, Group 3
  15: "#BA963C", // snoRNA
  16: "#DCAF3C", // snRNA
  17: "#F1C34F", // lncRNA
  18: "#FCD672", // ncRNA
  // Probes/Probesets
  9: "#333333", // Probe
  11: "#555555", // Probe set
  // Other
  7: "#CCEBC5", // 3'-UTR
  8: "#BCDBB5", // 5'-UTR
  10: "#ACCBA5", // DNA
  12: "#9CBB95", // EST
  13: "#8CAB85", // Read
};

export interface IColorSchemes {
  highlight: IColorSchemeForType; // idx for genome-browser-color.ts:ColorSchemes
}

const generateColorSetForMediumColor = (someColorFormat: string): IColorScheme => {
  const hslColor = parseToHsl(someColorFormat);
  const genLight = hsl(
    (((hslColor.hue - 1) % 360) + 360) % 360,
    Math.max(0, Math.min(1, 0.875 * hslColor.saturation)),
    Math.max(0, Math.min(1, 1.4 * hslColor.lightness)),
  );
  const genDark = hsl(
    hslColor.hue,
    Math.max(0, Math.min(1, 1.4375 * hslColor.saturation)),
    Math.max(0, Math.min(1, 0.8 * hslColor.lightness)),
  );
  const genDarker = hsl(
    hslColor.hue,
    Math.max(0, Math.min(1, 1.4375 * hslColor.saturation)),
    Math.max(0, Math.min(1, 0.4 * hslColor.lightness)),
  );

  return {
    darker: genDarker,
    dark: genDark,
    medium: hslToColorString(hslColor),
    light: genLight,
    lighter: genLight,
    lightest: "#fff",
  };
};

export const getColorScheme = (type: string, color = null): IColorSchemeForType => {
  const entry = FeatureType2ColorScheme.find((z: IKeyAndScheme) => z.key === type);

  if (!entry) {
    throw new Error("Cannot get color scheme for type '" + type + "'");
  }

  if (entry.isDynamic) {
    if (!color) {
      throw new Error("Reference color required for dynamic color set generation (type:'" + type + "')");
    }

    return {
      colorScheme: generateColorSetForMediumColor(color),
      type: type,
    };
  }

  return {
    colorScheme: ColorSchemes[entry.schemeIndex],
    type: type,
  };
};

export const expressionFeatureColorNoneFill = "RGB(255, 255, 255)";
export const expressionFeatureColorNoneStroke = "RGB(230, 230, 230)";
